/**
 * Which project the provider was last working in.
 *
 * ★★ WHY IT EXISTS (David, 2026-07-24: *"do you now remember the most recent active project and
 * auto select it at start up"*). It did not — a reload fell back to `list[0]`, and projects come
 * back NEWEST-first, so someone who had spent the afternoon in one project could return to a
 * different one entirely. That is the same class of surprise as the wrong-project bug: the screen
 * quietly showing something other than the thing being worked on.
 *
 * ⚠ A POINTER, NOT A SOURCE OF TRUTH. The stored key is only ever a suggestion — the project may
 * have been deleted, archived, or belong to a different account since. Every caller re-checks it
 * against the projects the SERVER just returned, and falls back if it is not there.
 */

const KEY = 'yoreflix.upload.project'

export function rememberProject(projectKey: string): void {
  try {
    if (projectKey === '') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, projectKey)
  } catch {
    // ⚠ Safari Private Browsing restricts persistent storage. Forgetting which project someone was
    // in is a small loss; throwing on the way into the screen is not.
  }
}

export function recallProject(): string {
  try {
    return localStorage.getItem(KEY) ?? ''
  } catch {
    return ''
  }
}

/**
 * Which project the screen should open on.
 *
 * ★★ THE ORDER IS THE DESIGN, and each step earns its place:
 *   1. **the project a restored queue belongs to** — there is unfinished work sitting in it, and
 *      showing anything else is exactly what made a completed upload look lost (see the
 *      wrong-project bug). Work in progress outranks preference.
 *   2. **the project they were last in** — the plain answer to "put me back where I was".
 *   3. **the newest project** — the old behaviour, and still right for a first visit.
 *
 * ⚠ Both remembered keys are validated against `available` before being trusted, so a deleted
 * project can never leave the screen pointing at nothing.
 */
export function projectToOpen(
  available: readonly { PrimaryKey: string }[],
  queueProjectKey: string,
  remembered: string,
): string {
  const exists = (key: string): boolean => key !== '' && available.some((p) => p.PrimaryKey === key)

  if (exists(queueProjectKey)) return queueProjectKey
  if (exists(remembered)) return remembered

  return available[0]?.PrimaryKey ?? ''
}
